import { useFocusEffect } from '@react-navigation/native';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LayoutRectangle } from 'react-native';
import {
  BackHandler,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View
} from 'react-native';
import {
  KeyboardController,
  useReanimatedKeyboardAnimation
} from 'react-native-keyboard-controller';
import { Portal } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { isWeb, isAndroid } from '@/shared/utils/platform';
import { webScrollBarStyle } from '@/styles/customStyles';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const CONTENT_GAP = 4;
const MIN_WIDTH = 180;
const MAX_HEIGHT = 300;
const HORIZONTAL_PADDING = 16;

const makeStyles = (backgroundColor: string) =>
  StyleSheet.create({
    shadow: {
      position: 'absolute',
      borderRadius: 4,
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
      elevation: 8,
      backgroundColor,
      minWidth: MIN_WIDTH
    },
    content: { borderRadius: 4, overflow: 'hidden' },
    anchor: { alignSelf: 'flex-start' },
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    },
    measureContainer: {
      position: 'absolute',
      top: -9999,
      left: -9999,
      opacity: 0,
      pointerEvents: 'none',
      minWidth: MIN_WIDTH
    }
  });

type MenuPosition = {
  top: number;
  left: number;
};

export type ExtendedMenuProps = PropsWithChildren<{
  visible: boolean;
  anchor: ReactNode;
  dismissKeyboard?: boolean;
  /**
   * by default theme.colors.surfaceContainerLow will be used
   */
  backgroundColor?: string;
  onDismiss: () => void;
}>;

export const ExtendedMenu: FC<ExtendedMenuProps> = ({
  visible,
  anchor,
  dismissKeyboard = false,
  backgroundColor,
  onDismiss,
  children
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(
    () => makeStyles(backgroundColor ?? theme.colors.surfaceContainerLow),
    [backgroundColor, theme.colors.surfaceContainerLow]
  );

  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

  const anchorRef = useRef<View>(null);
  const isOpeningRef = useRef(false);

  const [menuPosition, setMenuPosition] = useState<MenuPosition>();
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 });

  const containerWidth = screenWidth;

  const maxMenuWidth = containerWidth - 2 * HORIZONTAL_PADDING;
  const menuWidth = Math.min(
    Math.max(contentSize.width, MIN_WIDTH),
    maxMenuWidth
  );
  const menuHeight = Math.min(contentSize.height, MAX_HEIGHT);

  const handleClose = useCallback(() => {
    setMenuPosition(undefined);
    onDismiss();
  }, [onDismiss]);

  const calculateMenuPosition = useCallback(
    (currentKeyboardHeight: number) => {
      anchorRef.current?.measureInWindow((x, y, width, height) => {
        const anchorLayout: LayoutRectangle = {
          x,
          y: isAndroid ? y + insets.top : y,
          width,
          height
        };
        const availableHeight = screenHeight - currentKeyboardHeight;
        const minTop = insets.top;
        const maxBottom = availableHeight - anchorLayout.height;

        // Horizontal
        const anchorRight = anchorLayout.x + anchorLayout.width;
        const spaceRight = containerWidth - anchorRight;
        const opensLeft = spaceRight < menuWidth;

        let left = opensLeft ? anchorRight - menuWidth : anchorLayout.x;
        left = Math.max(
          HORIZONTAL_PADDING,
          Math.min(left, containerWidth - menuWidth - HORIZONTAL_PADDING)
        );

        // Vertical
        const spaceBelow = maxBottom - (anchorLayout.y + anchorLayout.height);
        const spaceAbove = anchorLayout.y - minTop;
        const requiredHeight = menuHeight + CONTENT_GAP;
        const opensUp =
          spaceBelow < requiredHeight && spaceAbove >= requiredHeight;

        let top = opensUp
          ? anchorLayout.y - menuHeight - CONTENT_GAP
          : anchorLayout.y + anchorLayout.height + CONTENT_GAP;
        top = Math.max(minTop, Math.min(top, maxBottom - menuHeight));

        setMenuPosition({ top, left });
        isOpeningRef.current = false;
      });
    },
    [containerWidth, insets.top, menuHeight, menuWidth, screenHeight]
  );

  const handleOpen = useCallback(async () => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;

    if (!isWeb && dismissKeyboard) {
      await KeyboardController.dismiss();
      requestAnimationFrame(() => calculateMenuPosition(0));
    } else {
      calculateMenuPosition(isWeb ? 0 : -keyboardHeight.value);
    }
  }, [dismissKeyboard, keyboardHeight, calculateMenuPosition]);

  useEffect(() => {
    if (visible && !menuPosition && !isOpeningRef.current) {
      handleOpen();
    } else if (!visible && menuPosition) {
      handleClose();
    }
  }, [visible, menuPosition, handleOpen, handleClose]);

  useFocusEffect(
    useCallback(() => {
      if (!menuPosition) return;

      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        () => {
          handleClose();
          return true;
        }
      );

      return () => subscription.remove();
    }, [handleClose, menuPosition])
  );

  return (
    <View>
      {/*
       * The wrapper must hug the anchor (not stretch to the parent width):
       * measureInWindow returns this wrapper's box, and the menu aligns to its
       * edges. A full-width wrapper around a small left-aligned trigger would
       * make the menu right-align to the wrapper's far edge instead of opening
       * under the trigger.
       */}
      <View ref={anchorRef} collapsable={false} style={styles.anchor}>
        {anchor}
      </View>
      <Portal>
        {/* Hidden measuring copy — excluded via aria-hidden so role-based
            selectors only see the real menu below. */}
        <View
          aria-hidden
          style={[styles.measureContainer, { maxWidth: maxMenuWidth }]}
          onLayout={(e) => {
            setContentSize(e.nativeEvent.layout);
          }}
        >
          {children}
        </View>
      </Portal>

      {menuPosition && (
        <Portal>
          {/* Backdrop: allows to close the menu when user taps somewhere on the screen */}
          <Pressable
            accessibilityRole="button"
            style={styles.backdrop}
            onPress={handleClose}
          />

          {/* Menu Content */}
          <View
            style={[
              styles.shadow,
              { maxHeight: menuHeight, maxWidth: maxMenuWidth },
              menuPosition
            ]}
          >
            <ScrollView
              style={[styles.content, webScrollBarStyle]}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              bounces={false}
            >
              {children}
            </ScrollView>
          </View>
        </Portal>
      )}
    </View>
  );
};
